import { Spacing } from '@standandforce/foundation';
import type { HTMLAttributes } from 'react';
import { forwardRef } from 'react';
interface SpacerProps extends HTMLAttributes<HTMLDivElement> {
  variant?: 'xxs' /* 4px */ | 'xs' /* 8px */ | 's' /* 12px */ | 'sm' /* 16px */ | 'm' /* 24px */ | 'l' /* 28px */ | 'xl' /* 32px */ | 'xxl' /* 48px */ | 'none' /* 0px */;
  size?: keyof typeof Spacing;
}

/**
 * Spacerコンポーネントは、レイアウト内のスペースを確保するために使用します。
 * @example
 * ```jsx
 * import Spacer from './Spacer';
 *
 * function App() {
 *   return (
 *     <div>
 *       <div>上の要素</div>
 *       <Spacer variant="s" />
 *       <div>下の要素</div>
 *     </div>
 *   );
 * }
 * ```
 */
const Spacer = forwardRef<HTMLDivElement, SpacerProps>(function Spacer({
  variant,
  size = 8,
  style,
  ...props
}, ref) {
  const variantStyles = {
    xxs: {
      height: Spacing[4],
      width: Spacing[4]
    },
    xs: {
      height: Spacing[8],
      width: Spacing[8]
    },
    s: {
      height: Spacing[12],
      width: Spacing[12]
    },
    sm: {
      height: Spacing[16],
      width: Spacing[16]
    },
    m: {
      height: Spacing[24],
      width: Spacing[24]
    },
    l: {
      height: Spacing[28],
      width: Spacing[28]
    },
    xl: {
      height: Spacing[32],
      width: Spacing[32]
    },
    xxl: {
      height: Spacing[48],
      width: Spacing[48]
    },
    none: {
      height: 0,
      width: '100%'
    }
  };
  const appliedStyles = variant ? variantStyles[variant] : {
    height: Spacing[size],
    width: Spacing[size]
  };
  return <div ref={ref} style={{
    ...appliedStyles,
    ...style
  }} role="presentation" aria-hidden="true" {...props} />;
});
export default Spacer;